import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { BsMicFill, BsMicMuteFill } from 'react-icons/bs';

export default function VoiceMicButton({ onTranscript, initialText = '', size = 18, edge = false, style = {} }) {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const baseTextRef = useRef(initialText);

  useEffect(() => {
    baseTextRef.current = initialText;
  }, [initialText]);

  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let fullTranscript = '';
      for (let i = 0; i < event.results.length; i++) {
        fullTranscript += event.results[i][0].transcript;
      }
      if (onTranscript) {
        const combined = baseTextRef.current ? `${baseTextRef.current} ${fullTranscript}` : fullTranscript;
        onTranscript(combined);
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error', event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (_) {}
      }
    };
  }, [onTranscript]);

  const toggleListening = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!recognitionRef.current) {
      alert("Voice input is not supported in this browser. Please use Google Chrome or Microsoft Edge.");
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      baseTextRef.current = initialText || '';
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (err) {
        console.error("Error starting speech recognition:", err);
      }
    }
  };

  return (
    <Tooltip title={isListening ? "Listening... Click to stop" : "Voice Typing (Speech to Text)"} arrow placement="top">
      <IconButton
        type="button"
        onClick={toggleListening}
        size="small"
        edge={edge}
        sx={{
          color: isListening ? '#ef4444' : '#64748b',
          backgroundColor: isListening ? '#fee2e2' : 'transparent',
          p: 0.75,
          borderRadius: '50%',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: isListening ? '#fecaca' : '#f1f5f9',
            color: isListening ? '#dc2626' : '#2563eb'
          },
          ...style
        }}
      >
        {isListening ? <BsMicFill size={size} /> : <BsMicMuteFill size={size} />}
      </IconButton>
    </Tooltip>
  );
}
